import { Box, IconButton, List, Modal, Skeleton, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GoogleMap, Libraries, useJsApiLoader } from '@react-google-maps/api';
import axios from 'axios';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  BrandclubStoreConfigUtils,
  brandclubStyles,
  CloseIcon,
  getBrandLogo,
  MOBILE_SCREEN_SIZE,
} from '@brandclub/common-ui';
import { getEntityImage } from '../../../utils';
import { MapIcon } from './MapIcon';
import { Store } from './types';
import { isSameStore, storeKey, storeLogoUrl } from './storeOwner';
import { useGeolocation } from './useGeolocation';

import { LocationIcon } from './LocationIcon';
import LocationListItem from './LocationListItem';
import { purchaseOnlineRow } from './purchaseOnlineRow';
import { usePurchaseOnlineShown } from './usePurchaseOnlineShown';
import SearchBar from './SearchBar';

const MapContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  width: '100%',
  height: '50%',
  [theme.breakpoints.up('md')]: {
    width: '441px',
    height: 'auto',
  },
  // Reset styles for Google Maps elements to prevent interference from global styles
  '& .gm-style': {
    all: 'revert',
  },
  '& .gm-control-active': {
    minWidth: '40px !important',
    width: '40px !important',
    height: '40px !important',
  },
}));

const LocationMapPopupContainer = styled(Box, {
  label: 'LocationMapPopupContainer',
  shouldForwardProp: (prop) => !['mobile', 'showMobileMap'].includes(prop as string),
})<{
  mobile: boolean;
  showMobileMap: boolean;
}>(({ theme, mobile, showMobileMap }) => ({
  position: mobile ? 'fixed' : 'absolute',
  top: mobile ? 0 : '50%',
  left: mobile ? 0 : '50%',
  transform: mobile ? 'none' : 'translate(-50%, -50%)',
  width: mobile ? '100vw' : '90vw',
  maxWidth: 987,
  maxHeight: mobile ? 'unset' : 796,
  height: mobile ? '100dvh' : '90vh',
  overscrollBehavior: 'contain',
  color: theme.vars.palette.text.primary,
  backgroundColor: theme.vars.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: mobile ? 0 : '10px',
  overflow: 'hidden',
  '.location-map-popup-header': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: mobile ? 'sticky' : 'relative',
    top: 0,
    zIndex: 2,
    paddingTop: mobile ? 'env(safe-area-inset-top)' : '28.7px',

    boxShadow: mobile ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
    marginBottom: mobile ? 0 : '28.7px',
    height: mobile ? 'calc(64px + env(safe-area-inset-top))' : 'auto',
    '.location-map-popup-header-title': {
      textAlign: 'center',
      fontWeight: 'bold',
      color: 'inherit',
      fontSize: mobile ? '18px' : '24px',
      letterSpacing: '0.48px',
    },
    '.location-map-popup-header-close-button': {
      position: 'absolute',
      top: '50%',
      right: mobile ? '20px' : '28.7px',
      transform: 'translateY(calc(-50% + 10px))',
      zIndex: 1,
      boxSizing: 'border-box',
      padding: 0,
      margin: 0,
      border: 'none',
      background: 'transparent',
      appearance: 'none',
      WebkitAppearance: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      height: '16px',
      width: '16px',
      maxWidth: '16px',
      minWidth: '16px',
      color: 'inherit',
      font: 'inherit',
      '&:focus': {
        outline: 'none',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
      },
    },
  },
  '.location-map-popup-body': {
    position: 'relative',
    zIndex: 0,
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    paddingTop: mobile ? '24px' : 0,
    paddingLeft: mobile ? '0px' : '28.5px',
    paddingRight: mobile ? '0px' : '38.6px',
    paddingBottom: mobile ? 'calc(30.5px + env(safe-area-inset-bottom))' : '30.5px',
    flexDirection: mobile ? 'column' : 'row',

    '.location-map-popup-body-left': {
      display: mobile && showMobileMap ? 'none' : 'flex',
      flexDirection: 'column',

      paddingRight: mobile ? 0 : '24px',
      width: mobile ? '100%' : '455px',
      marginBottom: mobile ? '20px' : 0,
      height: mobile ? '100%' : 'auto',
      visibility: mobile && showMobileMap ? 'hidden' : 'visible',
      position: mobile && showMobileMap ? 'absolute' : 'relative',

      '.location-map-popup-body-left-list': {
        flex: 1,
        overflow: 'auto',
        padding: mobile ? '0px 16px' : '0px',
        WebkitOverflowScrolling: 'touch',
        ...brandclubStyles.customScrollbar,

        '&::-webkit-scrollbar': {
          height: 9,
          width: 9,
          backgroundColor: 'transparent',
          // borderRadius: 20,
          position: 'absolute',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#E8E8ED',
          // borderRadius: 20,
          borderLeft: '4px solid #ffffff',
        },

        '.location-map-popup-body-left-list-skeleton': {
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          flexDirection: 'column',
          width: '100%',
        },
      },
    },
  },
}));

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const libraries: Libraries = ['places', 'marker', 'geocoding'];

// Add utility function to check if position has changed significantly
const hasSignificantPositionChange = (oldLat: number, oldLng: number, newLat: number, newLng: number): boolean => {
  // Round to 4 decimal places for comparison
  const roundToFour = (num: number) => Math.round(num * 10000) / 10000;
  return roundToFour(oldLat) !== roundToFour(newLat) || roundToFour(oldLng) !== roundToFour(newLng);
};

// Add type definition near the top of the file
type MarkerWithStore = google.maps.marker.AdvancedMarkerElement & {
  storeData?: Store;
};

const LocationMapInner = React.forwardRef<
  HTMLDivElement,
  {
    stacklineSku: string;
    product: any;
    API_URL?: string;
    onAddProductToBag?: () => void;
    mobile?: boolean;
    handleClose: () => void;
    checkoutRetailerIds?: number[];
  }
>(({ stacklineSku, API_URL, product, onAddProductToBag, mobile = false, handleClose, checkoutRetailerIds }, ref) => {
  const theme = useTheme();
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMarkerLibraryLoaded, setIsMarkerLibraryLoaded] = useState(false);
  const markersRef = useRef<MarkerWithStore[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isUserPanning = useRef(false);
  const selectedStoreRef = useRef<HTMLDivElement>(null);
  const [lastSearchResults, setLastSearchResults] = useState<Store[]>([]);
  // Retailers the shopper can actually buy this SKU from online — the app's own channel when
  // checkout is on, plus carrying retailers whose link out (WHERE_TO_BUY) is enabled. Served by
  // /store-locator/search for V2 apps only; undefined ⇒ V1, keep the legacy offer-derived list.
  const [onlineRetailerIds, setOnlineRetailerIds] = useState<number[] | undefined>(undefined);

  const { location, getLocation, hasCheckedPermission } = useGeolocation();
  const [initialCenter, setInitialCenter] = useState<{ lat: number; lng: number } | null>(null);

  const googleMapsMapId = BrandclubStoreConfigUtils.getGoogleMapsMapId();

  const [lastFetchedPosition, setLastFetchedPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Check location permission on mount
  useEffect(() => {
    getLocation();
  }, []);

  const fetchStores = useCallback(
    async (params: {
      stacklineSku: string;
      searchText?: string;
      userLat?: number;
      userLon?: number;
      radiusMiles?: number;
    }) => {
      try {
        setLoading(true);
        const API_URL_WITHOUT_TRAILING_SLASH = (API_URL?.endsWith('/') ? API_URL.slice(0, -1) : API_URL)?.replace(
          '/api-rewards',
          '',
        );
        const response = await axios.post(`${API_URL_WITHOUT_TRAILING_SLASH}/api-rewards/store-locator/search`, params);
        setOnlineRetailerIds(
          Array.isArray(response.data?.onlineRetailerIds) ? response.data.onlineRetailerIds : undefined,
        );
        const storeData = response.data?.stores || response.data || [];
        if (Array.isArray(storeData)) {
          setLastSearchResults(storeData);
          // If there's a selected store, ensure it stays at the top
          if (selectedStore) {
            const selectedStoreStillExists = storeData.some((store) => isSameStore(store, selectedStore));
            if (selectedStoreStillExists) {
              const filteredStores = storeData.filter((store) => !isSameStore(store, selectedStore));
              setStores([selectedStore, ...filteredStores]);
            } else {
              setStores(storeData);
              setSelectedStore(null);
            }
          } else {
            setStores(storeData);
          }
          return storeData;
        } else {
          console.error('Invalid store data format:', storeData);
          setStores([]);
          setLastSearchResults([]);
          return [];
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
        setStores([]);
        setLastSearchResults([]);
        return [];
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    },
    [API_URL, selectedStore],
  );

  // Set initial center once we have either location or stores
  useEffect(() => {
    if (!initialCenter) {
      if (location) {
        setInitialCenter(location);
      } else if (hasCheckedPermission && stores[0]?.location) {
        setInitialCenter({
          lat: stores[0].location?.lat,
          lng: stores[0].location.lon,
        });
      } else if (hasCheckedPermission) {
        // Only use Seattle as fallback if we've checked permission and have no other options
        setInitialCenter({ lat: 47.6062, lng: -122.3321 });
      }
    }
  }, [location, stores, hasCheckedPermission]);

  const onClose = () => {
    if (mobile && showMobileMap) {
      setShowMobileMap(false);
      return;
    }
    handleClose();
  };

  // Add effect to handle selected store when stores update
  useEffect(() => {
    if (selectedStore) {
      // Check if the selected store is still in the list
      const storeStillExists = stores.some((store) => isSameStore(store, selectedStore));

      if (!storeStillExists) {
        setSelectedStore(null);
      }
    }
  }, [stores]);

  const debouncedFetchStores = useCallback(
    debounce((params: { stacklineSku: string; userLat: number; userLon: number; radiusMiles: number }) => {
      fetchStores(params);
    }, 500),
    [fetchStores],
  );

  // Handle map idle - fetch stores when map stops moving
  const handleMapIdle = useCallback(() => {
    if (!map) return;

    const center = map.getCenter();
    if (!center) return;

    const newLat = center.lat();
    const newLng = center.lng();

    // Only fetch new stores if position has changed significantly or this is the first fetch
    if (
      !lastFetchedPosition ||
      hasSignificantPositionChange(lastFetchedPosition.lat, lastFetchedPosition.lng, newLat, newLng)
    ) {
      // If the map was moved by user interaction (not programmatically), unselect the store
      if (!isUserPanning.current && selectedStore) {
        const storePosition = selectedStore.location;
        if (storePosition && hasSignificantPositionChange(storePosition.lat, storePosition.lon, newLat, newLng)) {
          setSelectedStore(null);
        }
      }

      // Only fetch new stores if we're on desktop or in mobile map view
      if (!mobile || showMobileMap) {
        setLastFetchedPosition({ lat: newLat, lng: newLng });
        debouncedFetchStores({
          stacklineSku,
          userLat: newLat,
          userLon: newLng,
          radiusMiles: 30,
        });
      }
    }
  }, [map, debouncedFetchStores, mobile, showMobileMap, stacklineSku, lastFetchedPosition, selectedStore]);

  // Initial fetch when component mounts or location changes
  useEffect(() => {
    if (mobile && location) {
      // For mobile, use user's location
      fetchStores({
        stacklineSku,
        userLat: location.lat,
        userLon: location.lng,
        radiusMiles: 30,
      });
    } else if (!mobile && map) {
      // For desktop, use map center once map is loaded
      const center = map.getCenter();
      if (center) {
        fetchStores({
          stacklineSku,
          userLat: center.lat(),
          userLon: center.lng(),
          radiusMiles: 30,
        });
      }
    }
  }, [location, map, mobile, fetchStores, stacklineSku]);

  // Handle store selection
  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);

    // Immediately update the stores list to show selected store at the top
    if (lastSearchResults.length > 0) {
      const otherStores = lastSearchResults.filter((s) => !isSameStore(s, store));
      setStores([store, ...otherStores]);
    }

    if (mobile) {
      setShowMobileMap(true);
    }

    // Scroll the selected store into view with a small delay to ensure DOM update
    setTimeout(() => {
      selectedStoreRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 100);

    if (map && store.location?.lat != null && store.location?.lon != null) {
      const position = {
        lat: store.location.lat,
        lng: store.location.lon,
      };

      const currentCenter = map.getCenter();
      if (
        currentCenter &&
        hasSignificantPositionChange(currentCenter.lat(), currentCenter.lng(), position.lat, position.lng)
      ) {
        // Only set panning flag and animate if we actually need to move the map
        isUserPanning.current = true;
        map.panTo(position);
        map.setZoom(15);

        // Reset the panning flag after animation completes
        const listener = map.addListener('idle', () => {
          google.maps.event.removeListener(listener);
          setTimeout(() => {
            isUserPanning.current = false;
          }, 100);
        });
      }
    }
  };

  // Add helper function to create marker element
  const createMarkerElement = (store: Store) => {
    const markerDiv = document.createElement('div');
    markerDiv.style.position = 'relative';
    markerDiv.style.width = '32.765px';
    markerDiv.style.height = '37.263px';

    const markerSvg = document.createElement('div');
    markerSvg.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32.765" height="37.263" viewBox="0 0 32.765 37.263">
        <g data-name="Group 20115">
          <g data-name="Group 20114">
            <path data-name="Path 23049" d="m248.5 0 3.14 32.4a16.43 16.43 0 0 0 13.506-16.052A16.5 16.5 0 0 0 248.5 0z" transform="translate(-232.381)" style="fill:${theme.vars.palette.primary.main}"/>
            <path data-name="Path 23050" d="M60.479 16.382C60.479 7.335 54.159 0 46.363 0a16.385 16.385 0 0 0-3.087 32.472l2.082 4.169a1.123 1.123 0 0 0 2.009 0l2.082-4.169c6.282-1.445 11.03-8.099 11.03-16.09z" transform="translate(-30)" style="fill:${theme.vars.palette.primary.main}"/>
          </g>
        </g>
        <path data-name="Path 25560" d="M14.854 0A14.854 14.854 0 1 1 0 14.854 14.854 14.854 0 0 1 14.854 0z" transform="translate(1.528 1.553)" style="fill:#f3f4f5"/>
      </svg>
    `;
    markerSvg.style.position = 'absolute';
    markerSvg.style.top = '0';
    markerSvg.style.left = '0';

    const logoContainer = document.createElement('div');
    logoContainer.style.position = 'absolute';
    logoContainer.style.top = '4.5px';
    logoContainer.style.left = '4.5px';
    // Walmart specific handling
    if (store.retailerid === 2) {
      logoContainer.style.left = '4.1px';
    }
    const logoUrl = storeLogoUrl(store, getEntityImage);
    logoContainer.style.width = '24px';
    logoContainer.style.height = '24px';
    logoContainer.style.borderRadius = '50%';
    logoContainer.style.overflow = 'hidden';
    logoContainer.style.display = 'flex';
    logoContainer.style.alignItems = 'center';
    logoContainer.style.justifyContent = 'center';

    if (logoUrl) {
      const logo = document.createElement('img');
      logo.src = logoUrl;
      logo.alt = store.ownerLabel ?? store.storename;
      logo.style.width = '100%';
      logo.style.height = '100%';
      logo.style.objectFit = 'contain';
      logoContainer.appendChild(logo);
    }
    markerDiv.appendChild(markerSvg);
    markerDiv.appendChild(logoContainer);

    markerDiv.style.cursor = 'pointer';
    markerDiv.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))';
    markerDiv.style.transition = 'filter 0.2s ease-in-out';
    markerDiv.onmouseover = () => {
      markerDiv.style.filter = 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))';
    };
    markerDiv.onmouseout = () => {
      markerDiv.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))';
    };

    return markerDiv;
  };

  // Create markers when map is loaded and stores change
  useEffect(() => {
    if (!map || !isMarkerLibraryLoaded || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

    interface MarkerWithStore extends google.maps.marker.AdvancedMarkerElement {
      storeData?: Store;
    }

    // Create a map of existing markers using store unique key
    const existingMarkers = new Map<string, MarkerWithStore>(
      markersRef.current
        .map((marker: any) => {
          const storeData = (marker as MarkerWithStore).storeData;
          if (!storeData) return null;
          return [storeKey(storeData), marker as MarkerWithStore] as [string, MarkerWithStore];
        })
        .filter((entry): entry is [string, MarkerWithStore] => entry !== null),
    );

    const newMarkers: MarkerWithStore[] = [];

    stores.forEach((store) => {
      const key = storeKey(store);
      const position = {
        lat: store.location?.lat,
        lng: store.location?.lon,
      };

      // Reuse existing marker if available
      let marker = existingMarkers.get(key);
      if (marker) {
        marker.position = position;
        existingMarkers.delete(key);
        newMarkers.push(marker);
      } else {
        // Create new marker if needed
        const markerDiv = createMarkerElement(store);
        marker = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position,
          content: markerDiv,
          title: store.storename,
        }) as MarkerWithStore;

        // Attach store data to marker for future reference
        marker.storeData = store;

        marker.addListener('click', () => {
          handleStoreSelect(store);
        });

        newMarkers.push(marker);
      }
    });

    // Remove markers that are no longer needed
    existingMarkers.forEach((marker) => {
      if (marker) {
        marker.map = null;
      }
    });

    markersRef.current = newMarkers;
  }, [map, stores, isMarkerLibraryLoaded]);

  // Clean up markers when component unmounts or when map changes
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => {
        if (marker) {
          marker.map = null;
        }
      });
      markersRef.current = [];
    };
  }, [map]);

  const showPurchaseOnline = usePurchaseOnlineShown();
  const onlineRow = purchaseOnlineRow({ product, onlineRetailerIds, checkoutRetailerIds, showPurchaseOnline });

  const pinLocation = (lat: number, lng: number) => {
    if (map) {
      map.panTo({ lat, lng });
      map.setZoom(12);
      fetchStores({
        stacklineSku,
        userLat: lat,
        userLon: lng,
        radiusMiles: 31,
      });
    }
  };

  const handleMapLoad = (map: google.maps.Map) => {
    setMap(map);
    // Force marker recreation when map loads
    setIsMarkerLibraryLoaded(false);
    setTimeout(() => {
      setIsMarkerLibraryLoaded(true);
    }, 0);
  };

  return (
    <LocationMapPopupContainer mobile={mobile} showMobileMap={showMobileMap} ref={ref}>
      {!(mobile && showMobileMap) && (
        <Box className="location-map-popup-header">
          <Typography className="location-map-popup-header-title">Find a store near you</Typography>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            className="location-map-popup-header-close-button"
            aria-label="Close store locator"
          >
            <CloseIcon
              width={mobile ? 14 : 16}
              height={mobile ? 14 : 16}
              style={{
                width: mobile ? 14 : 16,
                height: mobile ? 14 : 16,
                color: 'inherit',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Box>
      )}

      <Box className="location-map-popup-body">
        <Box className="location-map-popup-body-left">
          <Box mb={mobile ? '14.4px' : '22px'} mt={'1.5px'} pl={mobile ? '16px' : '0px'} pr={mobile ? '16px' : '0px'}>
            <SearchBar pinLocation={pinLocation} />
          </Box>
          {mobile && stores.length > 0 ? (
            <Typography
              sx={(theme) => ({
                fontSize: '18px',
                fontWeight: 'bold',
                lineHeight: '12px',
                letterSpacing: '0.36px',
                pt: '8.2px',
                mb: '16.6px',
                pl: mobile ? '16px' : '0px',
                pr: mobile ? '16px' : '0px',
                color: theme.vars.palette.text.primary,
              })}
            >
              {stores.length} store
              {stores.length > 1 ? 's' : ''} near you
            </Typography>
          ) : null}

          <List className="location-map-popup-body-left-list">
            {loading && isInitialLoad ? (
              <Box className="location-map-popup-body-left-list-skeleton">
                {[1, 2, 3, 4, 5].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      width: { xs: '100%' },
                      padding: '24px 20px 24px 18px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.vars.palette.divider}`,
                      backgroundColor: 'inherit',
                    }}
                  >
                    <Box sx={{ flexShrink: 0, mr: 2.5 }}>
                      <Skeleton
                        variant="circular"
                        width={50}
                        height={50}
                        sx={{
                          backgroundColor: theme.vars.palette.tertiary.main,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          backgroundColor: theme.vars.palette.tertiary.main,
                          height: '17px',
                          mb: '8px',
                          borderRadius: 0.5,
                          maxWidth: '49px',
                          width: '100%',
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: '27px',
                          backgroundColor: theme.vars.palette.tertiary.main,
                          mb: '10px',
                          borderRadius: 0.5,
                          width: '100%',
                          maxWidth: '205px',
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: '25px',
                          backgroundColor: theme.vars.palette.tertiary.main,
                          borderRadius: 0.5,
                          width: '100%',
                          maxWidth: '231px',
                        }}
                      />
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: '31.4px',
                          width: '79px',
                          maxWidth: '100%',
                          backgroundColor: theme.vars.palette.tertiary.main,
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                {onlineRow ? (
                  <LocationListItem
                    variant="product"
                    product={onlineRow.product}
                    retailerIds={onlineRow.retailerIds}
                    selected={false}
                    store={null}
                    getEntityImage={getEntityImage}
                    mobile={mobile}
                    onClick={() => {
                      handleClose();
                      onAddProductToBag?.();
                    }}
                    getBrandLogo={getBrandLogo}
                  />
                ) : null}
                {stores.map((store) => (
                  <LocationListItem
                    key={storeKey(store)}
                    variant="store"
                    ref={selectedStore === store ? selectedStoreRef : null}
                    store={store}
                    product={null}
                    retailerIds={[]}
                    getBrandLogo={getBrandLogo}
                    mobile={mobile}
                    onClick={() => handleStoreSelect(store)}
                    selected={selectedStore ? isSameStore(selectedStore, store) : false}
                    getEntityImage={getEntityImage}
                  />
                ))}
              </>
            )}
          </List>
        </Box>

        <MapContainer
          sx={{
            borderRadius: mobile ? '0px' : '8px',
            overflow: 'hidden',
            display: mobile ? (showMobileMap ? 'block' : 'none') : 'block',
            position: mobile ? 'absolute' : 'relative',
            top: mobile ? 0 : 'auto',
            left: mobile ? 0 : 'auto',
            right: mobile ? 0 : 'auto',
            bottom: mobile ? 0 : 'auto',
            width: mobile ? '100%' : '441px',
            height: mobile ? '100%' : 'auto',
            zIndex: mobile ? (showMobileMap ? 1 : 0) : 'auto',
          }}
        >
          {/* Mobile map top bar */}
          {mobile && showMobileMap ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                paddingTop: 'env(safe-area-inset-top)',
                height: 'calc(64px + env(safe-area-inset-top))',
                backgroundColor: theme.vars.palette.background.paper,

                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                pl: '20px',
                pr: '20px',
                justifyContent: 'space-between',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box flex={1}></Box>
              <Typography
                flex={1}
                sx={(theme) => ({
                  fontWeight: 'bold',
                  color: theme.vars.palette.text.primary,
                  fontSize: '18px',
                  textAlign: 'center',
                })}
              >
                Nearby stores
              </Typography>
              <Box sx={{ display: 'flex', gap: '15.9px' }} flex={1} justifyContent={'flex-end'}>
                <IconButton
                  onClick={() => {
                    if (location && map) {
                      map.panTo({ lat: location.lat, lng: location.lng });
                      map.setZoom(15);
                    } else {
                      getLocation();
                    }
                  }}
                  sx={{
                    boxSizing: 'border-box',
                    padding: 0,
                    cursor: 'pointer',
                    height: '16px',
                    width: '16px',
                    maxWidth: '16px',
                    minWidth: '16px',
                  }}
                >
                  <LocationIcon
                    width={16}
                    height={16}
                    sx={(theme) => ({
                      width: '16px',
                      height: '16px',
                      color: theme.vars.palette.text.primary,
                    })}
                  />
                </IconButton>
                <Box
                  onClick={() => {
                    setShowMobileMap(false);
                  }}
                  sx={{
                    boxSizing: 'border-box',
                    padding: '11px',
                    margin: '-11px',
                    cursor: 'pointer',
                    height: '14px',
                    width: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloseIcon
                    width={14}
                    height={14}
                    sx={(theme) => ({
                      height: '14px',
                      width: '14px',
                      color: theme.vars.palette.text.primary,
                    })}
                  />
                </Box>
              </Box>
            </Box>
          ) : null}
          <GoogleMap
            mapContainerStyle={{
              ...mapContainerStyle,
              height: mobile && showMobileMap ? 'calc(100% - (64px + env(safe-area-inset-top)))' : '100%',
              marginTop: mobile && showMobileMap ? 'calc(64px + env(safe-area-inset-top))' : 0,
            }}
            center={initialCenter || { lat: 47.6062, lng: -122.3321 }}
            zoom={12}
            onLoad={handleMapLoad}
            onIdle={handleMapIdle}
            options={{
              mapId: googleMapsMapId,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              backgroundColor: 'inherit',
            }}
          />
        </MapContainer>
      </Box>
    </LocationMapPopupContainer>
  );
});

/**
 * Controlled modal surface for the store locator. Renders just the MUI `<Modal>`
 * + `LocationMapInner` so callers (e.g. the imperative `openStoreLocator` public
 * API) can drive `open`/`onClose` without also rendering the banner.
 */
export const LocationMapModal = React.memo<{
  open: boolean;
  onClose: () => void;
  stacklineSku?: string;
  product: any;
  API_URL?: string;
  onAddProductToBag?: () => void;
  mobile?: boolean;
  checkoutRetailerIds?: number[];
}>(({ open, onClose, stacklineSku, product, API_URL, onAddProductToBag, mobile: mobileProp = false, checkoutRetailerIds }) => {
  const isMobileViewport = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);
  const mobile = mobileProp || isMobileViewport;

  const googleMapsApiKey = BrandclubStoreConfigUtils.getGoogleMapsApiKey();
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'bc-google-maps',
    googleMapsApiKey,
    libraries,
  });

  if (loadError) {
    console.error('Google Maps load error:', loadError);
    return null;
  }

  if (!stacklineSku) return null;

  return (
    <Modal
      disableScrollLock
      open={open}
      sx={{
        '& .MuiModal-backdrop + .MuiModal-root': {
          outline: 'none',
        },
      }}
      onClose={onClose}
      aria-labelledby="store-locations-modal"
      disableAutoFocus
    >
      {isLoaded ? (
        <LocationMapInner
          stacklineSku={stacklineSku}
          product={product}
          API_URL={API_URL}
          onAddProductToBag={onAddProductToBag}
          mobile={mobile}
          handleClose={onClose}
          checkoutRetailerIds={checkoutRetailerIds}
        />
      ) : (
        <Box sx={{ width: '100%', height: '100%' }} />
      )}
    </Modal>
  );
});

const LocationMap = React.memo<{
  stacklineSku?: string;
  product: any;
  API_URL?: string;
  onAddProductToBag?: () => void;
  mobile?: boolean;
  checkoutRetailerIds?: number[];
}>(({ stacklineSku, API_URL, product, onAddProductToBag, mobile = false, checkoutRetailerIds }) => {
  const [open, setOpen] = useState(false);

  if (!stacklineSku) return null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '10px',
          fontSize: '16px !important',
          fontWeight: 'bold',
          letterSpacing: 0.16,
          paddingTop: '24px',
          paddingBottom: '24px',
          borderTop: '1px solid #dcdddc',
          borderBottom: '1px solid #dcdddc',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(true)}
      >
        <Box display="flex" flexDirection="row" justifyContent={'center'} gap="4px" width="100%">
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.vars.palette.text.primary,
            })}
          >
            Want to purchase in a store?
          </Box>
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.vars.palette.text.primary,
              fontWeight: 'bold',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            })}
          >
            View locations
          </Box>
        </Box>
      </Box>

      <LocationMapModal
        open={open}
        onClose={() => setOpen(false)}
        stacklineSku={stacklineSku}
        product={product}
        API_URL={API_URL}
        onAddProductToBag={onAddProductToBag}
        mobile={mobile}
        checkoutRetailerIds={checkoutRetailerIds}
      />
    </>
  );
});

export default LocationMap;
